import React, 
       { useState, 
         useEffect, 
         useCallback,
         useRef 
       }                                      from 'react'
import { Dialog,
         DialogContent,
         DialogContentText,
         DialogTitle,
         DialogActions,
         Button,
         Tabs,
         Tab,
         Box,
         Alert,
         useTheme,
         Typography,
       }                                      from '@mui/material'
import { BglDialog, 
         BglDialogTitle, 
         BglDialogActions 
       }                                      from 'components/dialog/BglDialogStyles'
import { BglPrimaryButton, 
         BglPrimaryOutlinedButton 
       }                                      from 'components/button/BglButtons'
import { SurveyQuestionForm }                 from './SurveyQuestionForm'
import { APIData, CONTENT_VERIFICATION_STATUS }                            from 'types/cms/frontendTypes'
import { ApiErrorInfo }                       from 'types'
import { SurveyQBUtils }                      from '../utils/SurveyQBUtils'
import { useDispatch, useSelector } from 'react-redux'
import { useGlobalConfig } from 'context/GlobalContext'
import { selectCMSSelectedPilot } from 'store/dataReducers/cmsPilotSlice'
import { SurveyQBApis } from 'apis/surveyQBAPI'
import { updateModifiedQuestionsAction } from 'store/dataReducers/surveyQBSlice'

export interface UpsertSurveyQuestionProps {
  mode: 'create' | 'edit'
  question?: APIData<'api::survey-question.survey-question'> // Draft/Modified version
  publishedQuestion?: APIData<'api::survey-question.survey-question'> // Published version
  isOpen: boolean
  onMessage: (message: string, severity: 'error' | 'success' | 'info' | 'warning') => void
  onClose: () => void
}

export const UpsertSurveyQuestion: React.FC<UpsertSurveyQuestionProps> = ({
  mode,
  question,
  publishedQuestion,
  isOpen,
  onClose,
  onMessage
}) => {
  const theme = useTheme()
  
  // Use ref to store submit function - refs are synchronous and don't cause re-renders
  const formSubmitFnRef = useRef<(() => void) | null>(null)
  
  // Track if form is ready to enable button (state for re-render)
  const [isFormReady, setIsFormReady] = useState(false)

  // Stable callback that form component calls when ready
  const handleFormReady = useCallback((submitFn: () => void) => {
    formSubmitFnRef.current = submitFn
    setIsFormReady(true)
  }, [])

  const { rc } = useGlobalConfig()
  const dispatch = useDispatch()
  const selectedPilot = useSelector(selectCMSSelectedPilot)!

  const [activeTab, setActiveTab] = useState(0)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingTabChange, setPendingTabChange] = useState<number | null>(null)
  const [error, setError] = useState<ApiErrorInfo>()
  const [isSaving, setIsSaving] = useState(false)


  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setActiveTab(0)
      setIsFormDirty(false)
      setError(undefined)
      setShowConfirmDialog(false)
      setPendingTabChange(null)
      setIsFormReady(false) // Reset ready state, form will set it when ready
      formSubmitFnRef.current = null // Clear ref
    } else {
      // Clear when dialog closes
      formSubmitFnRef.current = null
      setIsFormReady(false)
    }
  }, [isOpen])

  // Determine if published tab should be shown
  const showPublishedTab = mode === 'edit' && publishedQuestion

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (activeTab === newValue) return

    if (isFormDirty) {
      setPendingTabChange(newValue)
      setShowConfirmDialog(true)
    } else {
      setActiveTab(newValue)
    }
  }

  const handleConfirmTabChange = () => {
    if (pendingTabChange !== null) {
      setActiveTab(pendingTabChange)
      setPendingTabChange(null)
      setIsFormDirty(false)
    }
    setShowConfirmDialog(false)
  }

  const handleCancelTabChange = () => {
    setPendingTabChange(null)
    setShowConfirmDialog(false)
  }

  const handleFormStateChange = useCallback((isDirty: boolean) => {
    setIsFormDirty(isDirty)
  }, [])

  const handleFormSubmit = async (data: any) => {
    setIsSaving(true)
    setError(undefined)

    try {
      let resp
      if (mode === 'create') {
        resp = await SurveyQBApis.createQuestion(rc, selectedPilot.documentId, data);
      } else {
        // Edit mode - only send changed fields
        const diff = SurveyQBUtils.getQuestionDiff(data, question!)
        diff.documentId = question!.documentId
        resp = await SurveyQBApis.updateQuestions(rc, {
          utilityDocumentId : selectedPilot.documentId,
          pilotId : selectedPilot.pilotId,
          data : [diff] as any
        })
      }

      if (resp.error || resp.data?.error) {
        let errorMsg = resp.error?.message || resp.data?.error
        if (errorMsg && typeof errorMsg === 'object') {
          errorMsg = JSON.stringify(errorMsg)
        }
        onMessage(errorMsg, 'error')
        setIsFormDirty(true)
      } else {
        // Create mode returns { data: { question: ... } } (singular)
        // Edit mode returns { data: { questions: [...] } } (plural)
        const questions = mode === 'create' 
          ? [resp.data!.question] 
          : resp.data!.questions
        dispatch(updateModifiedQuestionsAction({
          questions
        }))
        onMessage('Question saved successfully', 'success')
        setIsFormDirty(false)
        onClose()
      }
    } catch (err: any) {
      setError({
        code: 'UNKNOWN_ERROR',
        message: err.message || 'An error occurred while saving the question',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    if (!isSaving && (!isFormDirty || window.confirm('You have unsaved changes. Are you sure you want to close?'))) {
      setIsFormDirty(false)
      setError(undefined)
      onClose()
    }
  }

  const getDialogTitle = () => {
    return mode === 'create' ? 'Create Question' : 'Edit Question'
  }

  const getTabLabel = (index: number) => {
    if (mode === 'create') {
      return 'Draft'
    }
    if (index === 0) {
      return question?.editStatus === 'MODIFIED' ? 'Modified' : 'Draft'
    }
    return 'Published'
  }

  return (
    <>
      <BglDialog
        open={isOpen}
        onClose={handleClose}
        fullWidth
        PaperProps={{
          sx: {
            minWidth: '600px',
            maxWidth: '70vw',
            maxHeight: '90vh',
          },
        }}
      >
        <BglDialogTitle
          title={getDialogTitle()}
          onClose={handleClose}
        />

        {mode === 'edit' && question && (
          <Box sx={{ marginLeft: '20px' }}>
            <Typography sx={{ mb: 2 }}>
              Modify the details for the question: "{question.title}"
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ px: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.message}
            </Alert>
          </Box>
        )}

        {showPublishedTab && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="inherit"
              slotProps={{
                indicator: {
                  sx: { backgroundColor: theme.palette.text.secondary },
                },
              }}
              sx={{
                '& .Mui-selected': {
                  color: theme.palette.text.secondary,
                },
              }}
            >
              <Tab label={getTabLabel(0)} />
              <Tab label={getTabLabel(1)} disabled={!publishedQuestion} />
            </Tabs>
          </Box>
        )}

        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          {activeTab === 0 && (
            <SurveyQuestionForm
              question={question}
              publishedQuestion={publishedQuestion}
              isReadOnly={question ? question.verificationStatus !== CONTENT_VERIFICATION_STATUS.TODO : false}
              onFormStateChange={handleFormStateChange}
              onSubmit={handleFormSubmit}
              onFormReady={handleFormReady}
            />
          )}
          {activeTab === 1 && showPublishedTab && (
            <SurveyQuestionForm
              question={publishedQuestion}
              publishedQuestion={publishedQuestion}
              isReadOnly={true}
              onFormStateChange={handleFormStateChange}
              onSubmit={handleFormSubmit}
            />
          )}
        </DialogContent>

        <BglDialogActions>
          <BglPrimaryOutlinedButton onClick={handleClose} disabled={isSaving}>
            Cancel
          </BglPrimaryOutlinedButton>
          {activeTab === 0 && (
            <BglPrimaryButton
              onClick={() => {
                if (formSubmitFnRef.current) {
                  formSubmitFnRef.current()
                }
              }}
              disabled={isSaving || !isFormDirty || !isFormReady}
              loading={isSaving}
            >
              {mode === 'create' ? 'Create Question' : 'Save Changes'}
            </BglPrimaryButton>
          )}
        </BglDialogActions>
      </BglDialog>

      {/* Confirm Dialog for Tab Switch */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelTabChange}
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to switch tabs? Your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTabChange}>Cancel</Button>
          <Button onClick={handleConfirmTabChange} color="primary" variant="contained">
            Switch Tabs
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
